import { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  creditTotals,
  licenseIsInfinite,
  renderRemainingCredits,
} from "./LicenceCredits";
import {
  LicenseStatus,
  PackStatus,
} from "../../redux/actions/licenses/licenceTypes";
import { PacksTooltipContent } from "./PacksTooltip";
import { unlinkLicenseUser } from "../../redux/actions/licenses/licenseListAsyncActions";
import { ThunkDispatch } from "redux-thunk";
import { GridDeleteIcon } from "@mui/x-data-grid-premium";
import { Add } from "@mui/icons-material";
import { TFunction } from "i18next";

import {
  Box,
  Button,
  Chip,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  focusedLicense,
  LicensesFilters,
  selectLicencesAndOwners,
  selectUsersWithNoLicenses,
  setLicenseId,
  setPreselectedFilterField,
  setShowFilterForm,
} from "../../redux/slices/licenses/licenseList";

import {
  FirstLetterUpperCase,
  getOperatorsFromAvailableFilters,
  applyLocalFilter,
} from "../helpers/helpersSearchs";
import CustomGeneralColumnHeader from "../customFilterComponent/customHeader";
import { LicenseStylesColums } from "./licenseStyles/LicenseColumns";
import {
  cellHelpIconSx,
  cellValueWithIconSx,
  creditBoxSx,
  creditLabelStyle,
  expiryChipStyle,
  noPendingStyle,
  packTooltipSlotProps,
  statusBadgeStyle,
  statusDotStyle,
} from "./licenseStyles/LicenceColumns.styles";

import HelpIcon from "@mui/icons-material/Help";
import getLicenceTranslation, {
  licenceSlugsDictionary,
} from "./licenceHelpers/licenceSlugs";

const UnlinkConfirmButton: FC<{
  disabled: boolean;
  onConfirm: () => Promise<any>;
  t: TFunction;
}> = ({ disabled, onConfirm, t }) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <Button
        disabled={disabled}
        color="error"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={LicenseStylesColums.buttonTextTransform}
      >
        <GridDeleteIcon />
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={LicenseStylesColums.popoverBox}>
          <Typography
            variant="body2"
            sx={LicenseStylesColums.popoverTypography}
          >
            {t("licenses.unlinkUser_confirm")}
          </Typography>
          <Box sx={LicenseStylesColums.popoverActionsBox}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setAnchor(null)}
              sx={LicenseStylesColums.buttonTextTransform}
            >
              {t("Common_options.Cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              sx={LicenseStylesColums.buttonTextTransform}
              onClick={() => {
                setAnchor(null);
                onConfirm();
              }}
            >
              {FirstLetterUpperCase(t("Common_options.confirm"))}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const useSetActions = () => {
  const dispatch = useDispatch();
  return {
    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(true));
      dispatch(setPreselectedFilterField("siteName"));
    },

    setLicenseId: (licenseId: string) => {
      dispatch(setLicenseId(licenseId));
    },
  };
};

const recogniceLicenseStatus = (
  expiryDate: string | null,
  activationDate: string | null,
  username: string | null,
  t: TFunction,
  status: number,
): { status: string; color: string; bg: string } | null => {
  if (!expiryDate) return null;

  const now = new Date();
  const expiry = new Date(expiryDate);
  const activation = activationDate ? new Date(activationDate) : null;

  if (!username)
    return {
      status: t("licenses.unassigned"),
      color: LicenseStylesColums.statusColors.unassigned.color,
      bg: LicenseStylesColums.statusColors.unassigned.bg,
    };
  if (expiry <= now)
    return {
      status: t("licenses.expired"),
      color: LicenseStylesColums.statusColors.expired.color,
      bg: LicenseStylesColums.statusColors.expired.bg,
    };
  if (activation && activation > now)
    return {
      status: t("licenses.upcoming"),
      color: LicenseStylesColums.statusColors.upcoming.color,
      bg: LicenseStylesColums.statusColors.upcoming.bg,
    };

  if (status === LicenseStatus.noLicense) {
    return {
      status: t("licenses.noLicense"),
      color: LicenseStylesColums.statusColors.noLicense.color,
      bg: LicenseStylesColums.statusColors.noLicense.bg,
    };
  }

  return {
    status: t("licenses.active"),
    color: LicenseStylesColums.statusColors.active.color,
    bg: LicenseStylesColums.statusColors.active.bg,
  };
};

export const useLicenseColumns = (
  t: TFunction,
  dispatch: ThunkDispatch<any, any, any>,
  tableOptions?: any[],
  activeFilter?: string,
) => {
  const { onOpenFilter } = useSetActions();
  const { i18n } = useTranslation();
  const { filterModel } = useSelector(LicensesFilters);
  const licenses = useSelector(selectLicencesAndOwners);
  const usersWithNoLicenses = useSelector(selectUsersWithNoLicenses);

  const [sortAlpha, setSortAlpha] = useState<"asc" | "desc" | null>(null);
  const [sortSite, setSortSite] = useState<"asc" | "desc" | null>(null);
  const [activeSort, setActiveSort] = useState<"username" | "siteName" | null>(
    null,
  );

  const toggleAlphaSort = (
    _field: string,
    direction: "asc" | "desc" | null,
  ) => {
    if (direction === null) {
      setActiveSort(null);
      setSortAlpha(null);
      return;
    }
    setActiveSort("username");
    setSortSite(null);
    setSortAlpha(direction);
  };

  const toggleSiteSort = (_field: string, direction: "asc" | "desc" | null) => {
    if (direction === null) {
      setActiveSort(null);
      setSortSite(null);
      return;
    }
    setActiveSort("siteName");
    setSortAlpha(null);
    setSortSite(direction);
  };

  let licenseColumns: any[] = [
    {
      field: "siteName",
      headerName: t("Dashboard.activityTable.site"),
      disableColumnMenu: true,
      width: 255,
      sortable: false,
      type: "string",
      filterOperators: getOperatorsFromAvailableFilters([
        "$ilike",
        "$eq",
        "$ne",
      ]),
      filter: "LOCAL",
      service: "text",
      renderHeader: () => (
        <CustomGeneralColumnHeader
          headerName={t("Dashboard.activityTable.site")}
          field={"siteName"}
          filterModel={filterModel}
          availableFilters={["$ilike", "$eq", "$ne"]}
          onOpenFilter={onOpenFilter}
          newOptions={tableOptions}
          backgroundColor="#fafafa"
          sortable={true}
          sortModel={
            activeSort === "siteName" && sortSite
              ? [{ field: "siteName", sort: sortSite }]
              : []
          }
          onSort={toggleSiteSort}
        />
      ),
      renderCell(params) {
        const license = params.row?.license;
        const siteName = params.row?.siteName;
        const username = params.row?.username;

        if (!params.row || !license) {
          return null;
        }

        if (siteName) {
          return <>{siteName}</>;
        }

        if (license.ownerGroupId && !username) {
          return <Skeleton variant="text" width="100%" height={40} />;
        }

        return null;
      },
    },
    {
      field: "licenseStatus",
      headerName: t("licenses.licensesStatus"),
      disableColumnMenu: true,
      width: 155,
      sortable: false,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        const licenseStatus = params.row?.licenseStatus;

        if (licenseStatus === LicenseStatus.noLicense) {
          return (
            <div
              style={statusBadgeStyle(
                LicenseStylesColums.statusColors.noLicense.color,
                LicenseStylesColums.statusColors.noLicense.bg,
              )}
            >
              <span
                className="dot"
                style={statusDotStyle(
                  LicenseStylesColums.statusColors.noLicense.color,
                )}
              ></span>
              {t("licenses.noLicense")}
            </div>
          );
        }

        if (!license) return null;
        const status = recogniceLicenseStatus(
          license.expiryDate ?? null,
          license.activationDate ?? null,
          license.username ?? null,
          t,
          license.statusConstantId,
        );

        if (license?.ownerGroupId && !license?.username) return <div />;

        return (
          <div style={statusBadgeStyle(status?.color ?? "", status?.bg ?? "")}>
            <span
              className="dot"
              style={statusDotStyle(status?.color ?? "")}
            ></span>
            {status?.status}
          </div>
        );
      },
    },
    {
      field: "remaining",
      headerName: t("licenses.remaining"),
      disableColumnMenu: true,
      width: 180,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        return renderRemainingCredits(license, t, i18n.language);
      },
    },
    {
      field: "consumption",
      headerName: t("licenses.consumption"),
      disableColumnMenu: true,
      width: 180,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        const isExpired =
          new Date(license.expiryDate).getTime() - Date.now() <= 0;
        if (isExpired) {
          return <Chip label={t("licenses.expired")} variant="outlined" />;
        }
        if (licenseIsInfinite(license)) return <span>∞</span>;
        const { consumed, purchased } = creditTotals(license);
        if (purchased <= 0) return <span style={noPendingStyle}>-</span>;
        const packs = license.packs ?? [];
        const available = license.availableCredits ?? 0;
        const emptiedPacks = packs.filter(
          (p) => p.statusConstantId === PackStatus.emptied,
        );
        const activePack = packs.find(
          (p) => p.statusConstantId === PackStatus.active,
        );
        const calcRows: { label: string; value: number }[] = [];
        if (emptiedPacks.length) {
          calcRows.push({
            label: t("licenses.calc_emptied_packs"),
            value: emptiedPacks.reduce((acc, p) => acc + p.initialCredits, 0),
          });
        }
        if (activePack) {
          calcRows.push({
            label: t("licenses.calc_active_consumed"),
            value: activePack.initialCredits - available,
          });
        } else if (available !== 0) {
          calcRows.push({
            label: t("licenses.calc_active_consumed"),
            value: -available,
          });
        }
        return (
          <Tooltip
            title={
              calcRows.length ? (
                <PacksTooltipContent
                  packs={[]}
                  t={t}
                  language={i18n.language}
                  calculation={{
                    rows: calcRows,
                    totalLabel: t("licenses.consumption"),
                    total: consumed,
                  }}
                />
              ) : (
                t("licenses.consumptionDesc")
              )
            }
            placement="bottom-start"
            arrow
            slotProps={calcRows.length ? packTooltipSlotProps : undefined}
          >
            <Box sx={creditBoxSx}>
              <Box sx={cellValueWithIconSx}>
                <span style={creditLabelStyle}>
                  {consumed.toLocaleString()}
                </span>
                <HelpIcon sx={cellHelpIconSx} />
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "totalPurchased",
      headerName: t("licenses.totalPurchased"),
      disableColumnMenu: true,
      width: 220,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        if (licenseIsInfinite(license)) return <span>∞</span>;
        const { purchased } = creditTotals(license);
        if (purchased <= 0) return <span style={noPendingStyle}>-</span>;
        if (!license.packs?.length) {
          return (
            <span style={creditLabelStyle}>{purchased.toLocaleString()}</span>
          );
        }
        return (
          <Tooltip
            title={
              <PacksTooltipContent
                packs={license.packs}
                t={t}
                language={i18n.language}
                calculation={{
                  rows: [],
                  totalLabel: t("licenses.totalPurchased"),
                  total: purchased,
                }}
              />
            }
            placement="bottom-start"
            arrow
            slotProps={packTooltipSlotProps}
          >
            <Box sx={cellValueWithIconSx}>
              <span style={creditLabelStyle}>{purchased.toLocaleString()}</span>
              <HelpIcon sx={cellHelpIconSx} />
            </Box>
          </Tooltip>
        );
      },
    },

    {
      field: "licenceType",
      headerName: t("licenses.licenceType"),
      disableColumnMenu: true,
      width: 220,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;

        const licenceSlug = license?.licenseTemplate?.slug as string;

        return (
          <span style={creditLabelStyle}>
            {t(getLicenceTranslation(licenceSlug))}
          </span>
        );
      },
    },

    {
      field: "activationDate",
      headerName: t("licenses.activation"),
      disableColumnMenu: true,
      width: 150,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        const activationValue = params.value ?? license?.activationDate ?? null;
        if (!activationValue || !license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        const activationDate = new Date(activationValue);
        return <span>{activationDate.toLocaleDateString(i18n.language)}</span>;
      },
    },
    {
      field: "expiryDate",
      headerName: t("licenses.expiration"),
      disableColumnMenu: true,
      width: 150,
      sortable: true,
      type: "string",
      renderCell(params) {
        const license = params.row?.license;
        const expiryValue = params.value ?? license?.expiryDate;
        if (!expiryValue || !license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;

        const oneMonthFromNow = new Date();
        oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

        const expiryDate = new Date(expiryValue);
        const isExpired =
          license.statusConstantId === LicenseStatus.expired ||
          expiryDate.getTime() - Date.now() <= 0;
        const isExpiringSoon = !isExpired && expiryDate < oneMonthFromNow;
        const daysLeft = Math.ceil(
          (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        );
        return (
          <>
            {(isExpired || isExpiringSoon) && (
              <Tooltip
                title={
                  isExpired
                    ? FirstLetterUpperCase(t("licenses.expired"))
                    : t("licenses.expiresDays", { count: daysLeft })
                }
              >
                <Chip style={expiryChipStyle(isExpired)} variant="filled" />
              </Tooltip>
            )}
            <span>{expiryDate.toLocaleDateString(i18n.language)}</span>
          </>
        );
      },
    },

    {
      field: "",
      headerName: t("licenses.addPack"),
      disableColumnMenu: true,
      width: 100,
      sortable: false,
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        const expiryValue = license?.expiryDate;
        const expiryDate = new Date(expiryValue);
        const isExpired =
          license.statusConstantId === LicenseStatus.expired ||
          expiryDate.getTime() - Date.now() <= 0;
        return licenseIsInfinite(params.row.license) ||
          !(params.row.license as any).hasPacks ? null : (
          <Button
            color="info"
            disabled={isExpired}
            onClick={() => dispatch(focusedLicense(params.row.id))}
          >
            <Add />
          </Button>
        );
      },
    },
    {
      field: "id",
      headerName: t("licenses.unlinkUser"),
      disableColumnMenu: true,
      width: 120,
      sortable: false,
      renderCell(params) {
        const license = params.row?.license;
        if (!license) return null;
        if (license?.ownerGroupId && !license?.username) return <div />;
        const expiryValue = license?.expiryDate;
        const expiryDate = new Date(expiryValue);
        const isExpired =
          license.statusConstantId === LicenseStatus.expired ||
          expiryDate.getTime() - Date.now() <= 0;
        if (!params.row?.license) return null;
        return (
          <UnlinkConfirmButton
            disabled={
              params.row.license.statusConstantId === LicenseStatus.expired ||
              !params.row.username ||
              params.row.isUserWithNoLicense ||
              isExpired
            }
            onConfirm={() =>
              (dispatch(unlinkLicenseUser(params.value)) as any).unwrap()
            }
            t={t}
          />
        );
      },
    },
  ];

  if (activeFilter === "usersNoLicenses") {
    licenseColumns = licenseColumns.filter((col) =>
      ["siteName", "licenseStatus"].includes(col.field),
    );
  }

  const licensesRows = useMemo<any[]>(() => {
    if (activeFilter === "usersNoLicenses") {
      const textFilterItems =
        filterModel?.items?.filter(
          (item) => item.service === "text" || item.service === "operator",
        ) ?? [];

      return usersWithNoLicenses
        .map((user) => {
          const userGroupId =
            user.groups?.find((g: any) => g.typeId === 107)?.id ?? null;
          const site = user.associatedGeoPoint?.[0]?.name ?? null;
          return {
            id: user.id,
            path: [`user_${user.id}`],
            username: user.username,
            siteName: site,
            licenseStatus: 125,
            activationDate: null,
            consumption: null,
            totalPurchased: null,
            remaining: null,
            expiryDate: null,
            license: "No license",
            isUserWithNoLicense: true,
            userId: user.id,
            userGroupId,
          };
        })
        .filter((row) =>
          textFilterItems.every((item) => applyLocalFilter(item, row)),
        )
        .sort((a, b) => {
          const aUnassigned = !a.username;
          const bUnassigned = !b.username;
          if (aUnassigned && !bUnassigned) return 1;
          if (!aUnassigned && bUnassigned) return -1;

          if (activeSort === "siteName" && sortSite) {
            const aKey = (a.siteName ?? "").toLowerCase();
            const bKey = (b.siteName ?? "").toLowerCase();
            return sortSite === "asc"
              ? aKey.localeCompare(bKey)
              : bKey.localeCompare(aKey);
          }

          if (activeSort === "username" && sortAlpha) {
            const aKey = (a.username ?? a.siteName ?? "").toLowerCase();
            const bKey = (b.username ?? b.siteName ?? "").toLowerCase();
            return sortAlpha === "asc"
              ? aKey.localeCompare(bKey)
              : bKey.localeCompare(aKey);
          }

          const aKey = (a.username ?? a.siteName ?? "").toLowerCase();
          const bKey = (b.username ?? b.siteName ?? "").toLowerCase();
          return aKey.localeCompare(bKey);
        });
    }

    return licenses
      .map((license) => {
        const isUnassigned = !license?.username;
        const usernameKey = license?.username ?? `unassigned_${license.id}`;
        const totals = creditTotals(license);
        return {
          id: license.id,
          path: isUnassigned ? [String(license.id)] : [usernameKey, license.id],
          username: license?.username,
          userId: (license as any)?.userId ?? null,
          siteName: license?.siteName,
          licenseStatus: recogniceLicenseStatus(
            license.expiryDate ?? null,
            license.activationDate ?? null,
            license?.username ?? null,
            t,
            license.statusConstantId,
          ),
          activationDate: license.activationDate,
          consumption: totals.consumed,
          totalPurchased: totals.purchased,
          remaining: totals.remaining,
          expiryDate: license.expiryDate,
          license: license,
        };
      })
      .sort((a, b) => {
        const aUnassigned = !a.username;
        const bUnassigned = !b.username;
        if (aUnassigned && !bUnassigned) return 1;
        if (!aUnassigned && bUnassigned) return -1;

        if (activeSort === "siteName" && sortSite) {
          const aKey = (a.siteName ?? "").toLowerCase();
          const bKey = (b.siteName ?? "").toLowerCase();
          return sortSite === "asc"
            ? aKey.localeCompare(bKey)
            : bKey.localeCompare(aKey);
        }

        if (activeSort === "username" && sortAlpha) {
          const aKey = (a.username ?? a.siteName ?? "").toLowerCase();
          const bKey = (b.username ?? b.siteName ?? "").toLowerCase();
          return sortAlpha === "asc"
            ? aKey.localeCompare(bKey)
            : bKey.localeCompare(aKey);
        }

        const aKey = (a.username ?? a.siteName ?? "").toLowerCase();
        const bKey = (b.username ?? b.siteName ?? "").toLowerCase();
        return aKey.localeCompare(bKey);
      });
  }, [
    licenses,
    usersWithNoLicenses,
    activeFilter,
    filterModel,
    activeSort,
    sortAlpha,
    sortSite,
    t,
  ]);

  return {
    licenseColumns,
    licensesRows,
    sortAlpha,
    toggleAlphaSort,
    activeSort,
  };
};
